import { Link } from "react-router-dom";
import React, { useState } from "react";
import Box from "@mui/material/Box";
import {
  Typography,
  Breadcrumbs,
  LinearProgress,
  CircularProgress,
  Paper,
  TableContainer,
  Tooltip,
  Chip,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Tabs,
  Tab,
} from "@mui/material";
import Icon from "@mui/material/Icon";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";

import axios from "axios";

import { useSelector } from "react-redux";
import { show as showSnackbar } from "../redux/slices/snackbar";
import { useAppDispatch } from "../hooks";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import DevicesIcon from '@mui/icons-material/Devices';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SimCardIcon from '@mui/icons-material/SimCard';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SignalCellular0BarIcon from '@mui/icons-material/SignalCellular0Bar';
import SignalCellular1BarIcon from '@mui/icons-material/SignalCellular1Bar';
import SignalCellular2BarIcon from '@mui/icons-material/SignalCellular2Bar';
import SignalCellular3BarIcon from '@mui/icons-material/SignalCellular3Bar';
import SignalCellular4BarIcon from '@mui/icons-material/SignalCellular4Bar';
import RouterIcon from '@mui/icons-material/Router';

// Custom TabPanel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

interface CombinedGatewayPort {
  deviceName: string;
  deviceId: string;
  deviceModel: string;
  deviceStatus: string;
  deviceLastUpdate: string;
  deviceCode: string; // 6 số cuối của tên device
  deviceLogs: string; // Gateway logs
  appVersion: string; // App version from gateway.attributes.appVersion
  portIndex: number;
  portMsisdn: string;
  portOperator: string;
  portBalance: string;
  portBalanceCurrency: string;
  portState: string;
  portSignal: number;
  portSignalBars: string;
  dailySentCount: number;
  dailySuccessCount: number;
  dailySuccessRate: number;
  lastSentAt: number;
  isBlocked: boolean;
  blockReasons: string[];
  nextSendTime?: number; // Timestamp - Thời gian sớm nhất được phép gửi tin tiếp theo
  minIntervalMinutes?: number; // Số phút random (10-18) đã được set
  lastUSSDMessage?: {
    content: string;
    time: string | number;
  };
}

interface MessageHistory {
  id: string;
  createdAt: string;
  changedAt: string;
  sentAt?: string;
  body: string;
  direction: string;
  status: string;
  to: string;
  from: string;
  gateway: {
    id: string;
    name: string;
    port: string;
  };
  data?: any;
}

export function GatewaysCombined() {
  const dispatch = useAppDispatch();
  const user = useSelector((state: any) => state.user);
  const itemName = `Gateway Overview`;
  const itemDescription = "Tổng quan các thiết bị Gateway và SIM theo danh sách.";

  const itemAPIRootPath = `${process.env.REACT_APP_MSG_API}/gateways`;
  let [items, setItems] = useState([]);
  let [combinedData, setCombinedData] = useState<CombinedGatewayPort[]>([]);
  let [isProcessing, setIsProcessing] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Dialog states
  const [selectedPort, setSelectedPort] = useState<CombinedGatewayPort | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [testMessage, setTestMessage] = useState("");
  const [testRecipient, setTestRecipient] = useState("0912345678");
  const [isSendingTest, setIsSendingTest] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [messageHistory, setMessageHistory] = useState<MessageHistory[]>([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [isUnblocking, setIsUnblocking] = useState(false);
  const [isBlocking, setIsBlocking] = useState(false);
  const [blockReason, setBlockReason] = useState("");
  const [isBlockDialogOpen, setIsBlockDialogOpen] = useState(false);

  const loadItems = (isShowSnackbar = true) => {
    let apiURL = `${itemAPIRootPath}?pageSize=200`;
    const axiosConfig = {
      method: "get",
      url: apiURL,
      headers: {
        Authorization: `bearer ${user.accessToken}`,
        "Content-Type": "application/json",
      },
      data: {},
    };

    setIsProcessing(true);
    axios(axiosConfig)
      .then(function (response) {
        if (isShowSnackbar) {
          dispatch(
            showSnackbar({ title: `Tải dữ liệu thành công!`, type: "success" })
          );
        }
        setItems(response.data.data);
        processCombinedData(response.data.data);
      })
      .catch(function (error) {
        if (isShowSnackbar) {
          dispatch(
            showSnackbar({
              title: `Tải dữ liệu lỗi!`,
              content: `${error.message}`,
              type: "error",
            })
          );
        }
      })
      .then(function () {
        setIsProcessing(false);
      });
  };

  const processCombinedData = (gatewayData: any[]) => {
    const combined: CombinedGatewayPort[] = [];

    gatewayData.forEach((gw: any) => {
      if (gw?.attributes?.ports) {
        gw.attributes.ports.forEach((p: any) => {
          const deviceCode = gw.name.slice(-6); // Lấy 6 số cuối
          const isBlocked = p.isBlocked || false;
          const portState = isBlocked ? "blocked" : (p.state || "idle");

          combined.push({
            deviceName: gw.name,
            deviceId: gw.id,
            deviceModel: gw.model || "",
            deviceStatus: gw.status || "unknown",
            deviceLastUpdate: gw.changedAt || new Date().toISOString(),
            deviceCode: deviceCode,
            deviceLogs: gw.attributes?.logs || "",
            appVersion: gw.attributes?.appVersion || "",
            portIndex: p.index || 0,
            portMsisdn: p.msisdn || "",
            portOperator: p.operator || "",
            portBalance: p.balance?.amount?.toString() || "0",
            portBalanceCurrency: p.balance?.currency || "VND",
            portState: portState,
            portSignal: p.signal || 0,
            portSignalBars: rssiToBars(p.signal || 0),
            dailySentCount: p.dailySentCount || 0,
            dailySuccessCount: p.dailySuccessCount || 0,
            dailySuccessRate: p.dailySuccessRate || 0,
            lastSentAt: p.lastSentAt || 0,
            isBlocked: isBlocked,
            blockReasons: p.blockReasons || [],
            nextSendTime: p.nextSendTime,
            minIntervalMinutes: p.minIntervalMinutes,
            lastUSSDMessage: p.lastUSSDMessage ? {
              content: p.lastUSSDMessage.content || "",
              time: p.lastUSSDMessage.time?.$date || p.lastUSSDMessage.time || 0
            } : undefined,
          });
        });
      }
    });

    setCombinedData(combined);
  };

  function rssiToBars(rssi: number): string {
    if (rssi === 99 || rssi === 0) return "0"; // Không có sóng
    if (rssi >= 1 && rssi <= 9) return "1"; // 1 cột
    if (rssi >= 10 && rssi <= 14) return "2"; // 2 cột
    if (rssi >= 15 && rssi <= 19) return "3"; // 3 cột
    if (rssi >= 20 && rssi <= 31) return "4"; // 4 cột
    return "0"; // Mặc định nếu giá trị không hợp lệ
  }

  function getSignalIcon(rssi: number) {
    const bars = parseInt(rssiToBars(rssi));
    const clampedBars = Math.max(0, Math.min(4, bars));

    switch (clampedBars) {
      case 0: return <SignalCellular0BarIcon fontSize="small" />;
      case 1: return <SignalCellular1BarIcon fontSize="small" />;
      case 2: return <SignalCellular2BarIcon fontSize="small" />;
      case 3: return <SignalCellular3BarIcon fontSize="small" />;
      case 4: return <SignalCellular4BarIcon fontSize="small" />;
      default: return <SignalCellular0BarIcon fontSize="small" />;
    }
  }

  function formatDeviceId(deviceId: string): string {
    if (!deviceId) return "N/A";
    // Lấy 4 ký tự cuối của device ID
    return `...${deviceId.slice(-4)}`;
  }

  function getTimeAgo(timestamp: number): string {
    if (!timestamp) return "Chưa gửi";

    const now = Date.now();
    const diff = now - timestamp;

    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    if (minutes > 0) {
      return `${minutes} phút ${seconds} giây trước`;
    } else {
      return `${seconds} giây trước`;
    }
  }

  function getTimeAgoHoursMinutes(timestamp: number | string): string {
    if (!timestamp) return "N/A";

    const time = typeof timestamp === 'string' ? new Date(timestamp).getTime() : timestamp;
    const now = Date.now();
    const diff = now - time;

    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  function translateBlockReason(reason: string): string {
    // Check if manual block
    if (reason.startsWith('manual_block:')) {
      const customReason = reason.replace('manual_block:', '').trim();
      return `🔒 Manual: ${customReason}`;
    }

    const translations: Record<string, string> = {
      'daily_success_limit': 'Đã gửi đủ 40 tin trong ngày',
      'time_interval_wait': 'Chưa đủ khoảng thời gian chờ',
      'daily_error_limit': 'Quá 10 tin lỗi trong ngày',
      'consecutive_errors': 'Quá 3 tin lỗi liên tiếp trong 30 phút'
    };
    return translations[reason] || reason;
  }

  function getCountdownToNextSend(nextSendTime: number): string {
    if (!nextSendTime) return "N/A";

    const now = Date.now();
    const diff = nextSendTime - now;

    if (diff <= 0) {
      return "Có thể gửi ngay";
    }

    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    } else {
      return `${seconds}s`;
    }
  }

  function getRowBackgroundColor(successRate: number, dailySentCount: number): string {
    if (dailySentCount > 0 && successRate < 50) {
      return "#ffebee"; // Đỏ nhạt
    } else if (dailySentCount > 0 && successRate < 70) {
      return "#fff8e1"; // Vàng nhạt
    }
    return "inherit"; // Màu bình thường
  }

  function getDeviceStatusColor(status: string): "success" | "warning" | "error" | "default" {
    switch (status.toLowerCase()) {
      case "active":
        return "success";
      case "inactive":
      case "offline":
        return "error";
      case "warning":
        return "warning";
      default:
        return "default";
    }
  }

  function getPortStateColor(state: string): "success" | "warning" | "error" | "default" {
    switch (state.toLowerCase()) {
      case "ready":
      case "enabled":
      case "active":
      case "idle":
        return "success";
      case "disabled":
      case "error":
      case "blocked":
        return "error";
      case "initializing":
      case "busy":
        return "warning";
      default:
        return "default";
    }
  }

  React.useEffect(() => {
    let interval: any = null;
    if (!isProcessing) {
      loadItems();
      interval = setInterval(() => {
        loadItems(false);
      }, 10000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, []);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sendTestMessage = async () => {
    if (!selectedPort || !testMessage.trim() || !testRecipient.trim()) {
      dispatch(
        showSnackbar({
          title: "Lỗi",
          content: "Vui lòng nhập đủ thông tin tin nhắn và số điện thoại",
          type: "error",
        })
      );
      return;
    }

    setIsSendingTest(true);

    try {
      const apiURL = `${process.env.REACT_APP_MSG_API}/messages`;
      const axiosConfig = {
        method: "post",
        url: apiURL,
        headers: {
          Authorization: `bearer ${user.accessToken}`,
          "Content-Type": "application/json",
        },
        data: {
          gateway: {
            id: selectedPort.deviceId,
            name: selectedPort.deviceName,
            port: (selectedPort.portIndex + 1).toString(),
          },
          to: testRecipient,
          body: testMessage,
          data: {
            TITLE: "Test",
            COMPANY: "Gateway Test",
            DEBT: "0"
          }
        },
      };

      const response = await axios(axiosConfig);

      if (response.data.id) {
        dispatch(
          showSnackbar({
            title: "Thành công",
            content: "Tin nhắn đã được xếp vào hàng đợi gửi",
            type: "success",
          })
        );
        // Refresh history after sending
        if (selectedPort) {
          loadMessageHistory(selectedPort);
        }
      }
    } catch (error: any) {
      dispatch(
        showSnackbar({
          title: "Lỗi",
          content: "Gửi tin thất bại vui lòng thử lại sau",
          type: "error",
        })
      );
    } finally {
      setIsSendingTest(false);
    }
  };

  const loadMessageHistory = async (port: CombinedGatewayPort) => {
    setIsLoadingHistory(true);
    try {
      const apiURL = `${process.env.REACT_APP_MSG_API}/messages?gateway.name=${port.deviceName}&gateway.port=${port.portIndex}&pageSize=20`;
      const axiosConfig = {
        method: "get",
        url: apiURL,
        headers: {
          Authorization: `bearer ${user.accessToken}`,
          "Content-Type": "application/json",
        },
      };

      const response = await axios(axiosConfig);
      setMessageHistory(response.data.data || []);
    } catch (error: any) {
      console.error("Error loading message history:", error);
      setMessageHistory([]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleUnblockPort = async () => {
    if (!selectedPort) return;

    setIsUnblocking(true);
    try {
      const apiURL = `${process.env.REACT_APP_MSG_API}/gateways/${selectedPort.deviceId}/port/${selectedPort.portIndex}/unblock`;
      const axiosConfig = {
        method: "post",
        url: apiURL,
        headers: {
          Authorization: `bearer ${user.accessToken}`,
          "Content-Type": "application/json",
        },
      };

      const response = await axios(axiosConfig);

      if (response.data.success) {
        dispatch(
          showSnackbar({
            title: "Thành công",
            content: response.data.message || `Port ${selectedPort.portIndex} đã được mở khóa`,
            type: "success",
          })
        );
        // Refresh gateway data to update UI
        loadItems(false);
        // Close dialog and refresh
        handleCloseDialog();
      }
    } catch (error: any) {
      dispatch(
        showSnackbar({
          title: "Lỗi",
          content: error.response?.data?.message || "Mở khóa port thất bại",
          type: "error",
        })
      );
    } finally {
      setIsUnblocking(false);
    }
  };

  const handleBlockPort = async () => {
    if (!selectedPort || !blockReason.trim()) {
      dispatch(
        showSnackbar({
          title: "Lỗi",
          content: "Vui lòng nhập lý do chặn port",
          type: "error",
        })
      );
      return;
    }

    setIsBlocking(true);
    try {
      const apiURL = `${process.env.REACT_APP_MSG_API}/gateways/${selectedPort.deviceId}/port/${selectedPort.portIndex}/block`;
      const axiosConfig = {
        method: "post",
        url: apiURL,
        headers: {
          Authorization: `bearer ${user.accessToken}`,
          "Content-Type": "application/json",
        },
        data: {
          reason: blockReason.trim()
        },
      };

      const response = await axios(axiosConfig);

      if (response.data.success) {
        dispatch(
          showSnackbar({
            title: "Thành công",
            content: response.data.message || `Port ${selectedPort.portIndex} đã được chặn`,
            type: "success",
          })
        );
        // Close block dialog
        setIsBlockDialogOpen(false);
        setBlockReason("");
        // Refresh gateway data to update UI
        loadItems(false);
        // Close main dialog
        handleCloseDialog();
      }
    } catch (error: any) {
      dispatch(
        showSnackbar({
          title: "Lỗi",
          content: error.response?.data?.message || "Chặn port thất bại",
          type: "error",
        })
      );
    } finally {
      setIsBlocking(false);
    }
  };

  const handleOpenBlockDialog = () => {
    setBlockReason("");
    setIsBlockDialogOpen(true);
  };

  const handleCloseBlockDialog = () => {
    setIsBlockDialogOpen(false);
    setBlockReason("");
  };

  // Dialog handlers
  const handleOpenDialog = (port: CombinedGatewayPort) => {
    setSelectedPort(port);
    setIsDialogOpen(true);
    setTestMessage("test");
    setTestRecipient("0912345678");
    setTabValue(0); // Reset to first tab
    loadMessageHistory(port);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedPort(null);
    setTestMessage("test");
    setTestRecipient("0912345678");
    setTabValue(0);
    setMessageHistory([]);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getStatusColor = (status: string): "success" | "warning" | "error" | "default" | "info" => {
    switch (status.toLowerCase()) {
      case "sent":
        return "success";
      case "queued":
      case "pending":
        return "warning";
      case "failed":
      case "error":
        return "error";
      case "delivered":
        return "info";
      default:
        return "default";
    }
  };

  // Calculate statistics
  const totalDevices = items.length;
  const activeDevices = items.filter((gw: any) => gw.status === "active").length;

  // Calculate SIM statistics by operator
  const vpnSIMs = combinedData.filter((p) =>
    p.portOperator?.toLowerCase() === "vinaphone" || p.portOperator?.toLowerCase() === "vina"
  ).length;
  const mbfSIMs = combinedData.filter((p) =>
    p.portOperator?.toLowerCase() === "mobifone" || p.portOperator?.toLowerCase() === "mobi"
  ).length;
  const vtlSIMs = combinedData.filter((p) =>
    p.portOperator?.toLowerCase() === "viettel" || p.portOperator?.toLowerCase() === "vettel"
  ).length;
  const vnmSIMs = combinedData.filter((p) =>
    p.portOperator?.toLowerCase() === "vietnamobile" || p.portOperator?.toLowerCase() === "vnm"
  ).length;

  const paginatedData = combinedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <React.Fragment>
      <Breadcrumbs aria-label="breadcrumb">
        <Link color="inherit" to="/">
          Trang Chủ
        </Link>
        <Link color="inherit" to="/gateways">
          Quản lý Gateway
        </Link>
        <Typography color="text.primary">{itemName}</Typography>
      </Breadcrumbs>

      <Typography variant="h1" className="page-title">
        {itemName}
      </Typography>
      <Typography variant="subtitle1">{itemDescription}</Typography>

      <LinearProgress
        sx={{ visibility: isProcessing ? "visible" : "hidden" }}
      />

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Card 1: Thiết bị */}
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Thiết bị
              </Typography>
              <Box display="flex" justifyContent="space-around" alignItems="center">
                <Box textAlign="center">
                  <Typography variant="h3" color="primary">
                    {totalDevices}
                  </Typography>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                    <DevicesIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Tổng
                    </Typography>
                  </Box>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h3" color="success.main">
                    {activeDevices}
                  </Typography>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                    <CheckCircleIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Hoạt động
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Card 2: SIM theo nhà mạng */}
        <Grid item xs={12} sm={6}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                SIM theo nhà mạng
              </Typography>
              <Box display="flex" justifyContent="space-around" alignItems="center">
                <Box textAlign="center">
                  <Typography variant="h3" color="warning.main">
                    {vpnSIMs}
                  </Typography>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                    <SimCardIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      VPN
                    </Typography>
                  </Box>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h3" color="info.main">
                    {mbfSIMs}
                  </Typography>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                    <SimCardIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      MBF
                    </Typography>
                  </Box>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h3" color="success.main">
                    {vtlSIMs}
                  </Typography>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                    <SimCardIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      VTL
                    </Typography>
                  </Box>
                </Box>
                <Box textAlign="center">
                  <Typography variant="h3" color="secondary.main">
                    {vnmSIMs}
                  </Typography>
                  <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                    <SimCardIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      VNM
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Combined Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: '70vh' }}>
          <Table stickyHeader aria-label="combined gateway port table">
            <TableHead>
              <TableRow>
                <TableCell variant="head">Device</TableCell>
                <TableCell variant="head">Status</TableCell>
                <TableCell variant="head">App</TableCell>
                <TableCell variant="head">SIM</TableCell>
                <TableCell variant="head">Số dư</TableCell>
                <TableCell variant="head">Thành công</TableCell>
                <TableCell variant="head">Cập nhật</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row, index) => (
                <TableRow
                  key={`${row.deviceId}-${row.portIndex}`}
                  hover
                  sx={{
                    backgroundColor: getRowBackgroundColor(row.dailySuccessRate, row.dailySentCount)
                  }}
                >
                  {/* Device */}
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="bold">
                        {formatDeviceId(row.deviceName)} - Sim {row.portIndex}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {row.deviceModel}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Status - Gộp device status và SIM status */}
                  <TableCell>
                    <Box>
                      <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
                        <RouterIcon fontSize="small" color="action" />
                        <Chip
                          label={row.deviceStatus}
                          color={getDeviceStatusColor(row.deviceStatus)}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <SimCardIcon fontSize="small" color="action" />
                        <Chip
                          label={row.portState}
                          color={getPortStateColor(row.portState)}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                  </TableCell>

                  {/* App Version */}
                  <TableCell>
                    <Typography variant="body2">
                      {row.appVersion || "N/A"}
                    </Typography>
                  </TableCell>

                  {/* SIM - Gộp số SIM, nhà mạng và sóng */}
                  <TableCell>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {row.portMsisdn || "N/A"}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="caption" color="text.secondary">
                            {row.portOperator || "N/A"}
                          </Typography>
                          <Tooltip title={`RSSI: ${row.portSignal}/31`}>
                            {getSignalIcon(row.portSignal)}
                          </Tooltip>
                        </Box>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(row)}
                        color="primary"
                      >
                        <InfoIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>

                  {/* Số dư */}
                  <TableCell>
                    <Typography variant="body2">
                      {row.portBalance} {row.portBalanceCurrency}
                    </Typography>
                  </TableCell>

                  {/* Thành công */}
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {row.dailySuccessRate.toFixed(1)}%
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {row.dailySuccessCount}/{row.dailySentCount}
                      </Typography>
                    </Box>
                  </TableCell>

                  {/* Cập nhật */}
                  <TableCell>
                    <Box>
                      <Typography variant="caption" display="block">
                        Cập nhật: {getTimeAgoHoursMinutes(row.deviceLastUpdate)}
                      </Typography>
                      <Typography variant="caption" display="block">
                        Gửi tin: {getTimeAgoHoursMinutes(row.lastSentAt)}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100, 200]}
          component="div"
          count={combinedData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Số dòng mỗi trang:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} của ${count !== -1 ? count : `hơn ${to}`}`
          }
        />
      </Paper>

      {/* Dialog chi tiết SIM */}
      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '500px' }
        }}
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              Chi tiết SIM - {selectedPort?.portMsisdn || "N/A"}
            </Typography>
            <IconButton onClick={handleCloseDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ pb: 0 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange}>
              <Tab label="Thông tin chi tiết" />
              <Tab label="Lịch sử tin nhắn" />
              <Tab label="Log" />
            </Tabs>
          </Box>

          {/* Tab 1: Thông tin chi tiết */}
          <TabPanel value={tabValue} index={0}>
            {selectedPort && (
                <Grid container spacing={2}>
                  {/* Thông tin Device */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                      Thông tin Gateway
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Device ID:</strong> {selectedPort.deviceId}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Device Name:</strong> {selectedPort.deviceName}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Model:</strong> {selectedPort.deviceModel}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Status:</strong>
                          <Chip
                            label={selectedPort.deviceStatus}
                            color={getDeviceStatusColor(selectedPort.deviceStatus)}
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Thông tin SIM */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1, mt: 2 }}>
                      Thông tin SIM
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Port:</strong> {selectedPort.portIndex + 1}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Số điện thoại:</strong> {selectedPort.portMsisdn || "N/A"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Nhà mạng:</strong> {selectedPort.portOperator || "N/A"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Số dư:</strong> {selectedPort.portBalance} {selectedPort.portBalanceCurrency}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Trạng thái:</strong>
                          <Chip
                            label={selectedPort.portState}
                            color={getPortStateColor(selectedPort.portState)}
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Sóng:</strong> {selectedPort.portSignal}/31
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Bị chặn:</strong> {selectedPort.isBlocked ? "Có" : "Không"}
                        </Typography>
                      </Grid>
                      {selectedPort.isBlocked && selectedPort.blockReasons.length > 0 && (
                        <Grid item xs={12}>
                          <Typography variant="body2">
                            <strong>Lý do chặn:</strong>
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            {selectedPort.blockReasons.map((reason, index) => (
                              <Chip
                                key={index}
                                label={translateBlockReason(reason)}
                                size="small"
                                color="error"
                                variant="outlined"
                                sx={{ mr: 0.5, mb: 0.5 }}
                              />
                            ))}
                          </Box>
                        </Grid>
                      )}
                      {selectedPort.nextSendTime && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2">
                            <strong>Có thể gửi lại sau:</strong> {getCountdownToNextSend(selectedPort.nextSendTime)}
                          </Typography>
                        </Grid>
                      )}
                      {selectedPort.minIntervalMinutes && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant="body2">
                            <strong>Khoảng chờ:</strong> {selectedPort.minIntervalMinutes} phút
                          </Typography>
                        </Grid>
                      )}
                      {selectedPort.lastUSSDMessage && (
                        <>
                          <Grid item xs={12}>
                            <Typography variant="body2">
                              <strong>USSD Message:</strong>
                            </Typography>
                            <Paper
                              elevation={0}
                              sx={{
                                p: 1.5,
                                mt: 0.5,
                                backgroundColor: '#f5f5f5',
                                border: '1px solid #e0e0e0'
                              }}
                            >
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {selectedPort.lastUSSDMessage.content}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                Cập nhật: {getTimeAgo(
                                  typeof selectedPort.lastUSSDMessage.time === 'string'
                                    ? new Date(selectedPort.lastUSSDMessage.time).getTime()
                                    : selectedPort.lastUSSDMessage.time
                                )}
                              </Typography>
                            </Paper>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </Grid>

                  {/* Thống kê gửi tin */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 1, mt: 2 }}>
                      Thống kê gửi tin (hôm nay)
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2">
                          <strong>Tổng đã gửi:</strong> {selectedPort.dailySentCount}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2">
                          <strong>Thành công:</strong> {selectedPort.dailySuccessCount}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Typography variant="body2">
                          <strong>Tỷ lệ thành công:</strong> {selectedPort.dailySuccessRate.toFixed(1)}%
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography variant="body2">
                          <strong>Gửi gần nhất:</strong> {getTimeAgo(selectedPort.lastSentAt)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Grid>

                  {/* Block/Unblock Port Section */}
                  <Grid item xs={12}>
                    {selectedPort.isBlocked ? (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          mt: 2,
                          backgroundColor: '#fff3e0',
                          border: '1px solid #ffb74d'
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight="bold" color="error" sx={{ mb: 1 }}>
                          ⚠️ Port đang bị chặn
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          Port này đang bị chặn và không thể gửi tin nhắn. Bạn có thể mở khóa để cho phép gửi tin ngay lập tức.
                        </Typography>
                        <Button
                          variant="contained"
                          color="warning"
                          onClick={handleUnblockPort}
                          disabled={isUnblocking}
                          startIcon={isUnblocking ? <CircularProgress size={16} /> : <Icon>lock_open</Icon>}
                        >
                          {isUnblocking ? "Đang mở khóa..." : "Mở khóa Port"}
                        </Button>
                      </Paper>
                    ) : (
                      <Paper
                        elevation={0}
                        sx={{
                          p: 2,
                          mt: 2,
                          backgroundColor: '#f5f5f5',
                          border: '1px solid #e0e0e0'
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                          Quản lý Port
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          Bạn có thể chặn port này thủ công nếu cần (ví dụ: SIM hết tiền, bảo trì, thay SIM mới...)
                        </Typography>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={handleOpenBlockDialog}
                          startIcon={<Icon>block</Icon>}
                        >
                          Chặn Port
                        </Button>
                      </Paper>
                    )}
                  </Grid>

                  {/* Form gửi tin test */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 2, mt: 2 }}>
                      Gửi tin nhắn test
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Số điện thoại nhận"
                          value={testRecipient}
                          onChange={(e) => setTestRecipient(e.target.value)}
                          variant="outlined"
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Nội dung tin nhắn"
                          value={testMessage}
                          onChange={(e) => setTestMessage(e.target.value)}
                          variant="outlined"
                          size="small"
                          multiline
                          rows={3}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <Button
                          variant="contained"
                          onClick={sendTestMessage}
                          disabled={isSendingTest}
                          startIcon={isSendingTest ? <CircularProgress size={16} /> : <Icon>send</Icon>}
                        >
                          {isSendingTest ? "Đang gửi..." : "Gửi tin nhắn"}
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </TabPanel>

          {/* Tab 2: Lịch sử tin nhắn */}
          <TabPanel value={tabValue} index={1}>
            {isLoadingHistory ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell variant="head">Thời gian</TableCell>
                        <TableCell variant="head">Đến</TableCell>
                        <TableCell variant="head">Nội dung</TableCell>
                        <TableCell variant="head">Trạng thái</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {messageHistory.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              Chưa có lịch sử tin nhắn
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        messageHistory.map((message) => (
                          <TableRow key={message.id} hover>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDateTime(message.sentAt || message.createdAt)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="medium">
                                {message.to}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {message.body}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={message.status}
                                color={getStatusColor(message.status)}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </TabPanel>

          {/* Tab 3: Log */}
          <TabPanel value={tabValue} index={2}>
            {selectedPort && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  backgroundColor: '#1e1e1e',
                  color: '#d4d4d4',
                  fontFamily: 'monospace',
                  fontSize: '0.875rem',
                  maxHeight: '500px',
                  overflowY: 'auto',
                  border: '1px solid #333'
                }}
              >
                <Typography
                  component="pre"
                  sx={{
                    margin: 0,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'inherit',
                    fontSize: 'inherit'
                  }}
                >
                  {selectedPort.deviceLogs || 'Không có log'}
                </Typography>
              </Paper>
            )}
          </TabPanel>
        </DialogContent>

        <DialogActions sx={{ pt: 0 }}>
          <Button onClick={handleCloseDialog} variant="outlined">
            Đóng
          </Button>
        </DialogActions>
      </Dialog>

      {/* Block Port Reason Dialog */}
      <Dialog
        open={isBlockDialogOpen}
        onClose={handleCloseBlockDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Chặn Port {selectedPort ? selectedPort.portIndex : ''}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Vui lòng nhập lý do chặn port này. Lý do sẽ được lưu lại để theo dõi.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Lý do chặn"
            placeholder="Ví dụ: SIM hết tiền, chờ nạp thêm"
            value={blockReason}
            onChange={(e) => setBlockReason(e.target.value)}
            variant="outlined"
            helperText="Nhập lý do cụ thể để dễ quản lý sau này"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBlockDialog} variant="outlined">
            Hủy
          </Button>
          <Button
            onClick={handleBlockPort}
            variant="contained"
            color="error"
            disabled={isBlocking || !blockReason.trim()}
            startIcon={isBlocking ? <CircularProgress size={16} /> : <Icon>block</Icon>}
          >
            {isBlocking ? "Đang chặn..." : "Chặn Port"}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}